import { useEffect, useMemo, useState } from "react"

import type { MetricRecord } from "@/metrics/runtime"
import {
  Input,
  SelectDropdown,
  Switch,
  type SelectDropdownOption,
} from "@coval/ui"

import type { ComparisonPlotScale } from "@/utils/comparisonPlot"

import type { PlotConfigControlsProps } from "../types"
import {
  CONSERVATIVE_OUTLIER_IQR_MULTIPLIER,
  DEFAULT_OUTLIER_IQR_MULTIPLIER,
} from "../utils/outliers"
import { DEFAULT_POW_EXPONENT } from "../utils/resolveScale"

// Past this many Compare By groups the footer legend likely exceeds its
// two-line cap, so the panel shows a heads-up next to the toggle.
const LEGEND_GROUP_WARN_THRESHOLD = 10

// Scatter X/Y axes plot numeric (float) or categorical (string) metrics; the
// bubble-size axis needs a numeric value, so it excludes categoricals.
const SCALE_OPTIONS: SelectDropdownOption[] = [
  { value: "linear", label: "Linear" },
  { value: "log", label: "Logarithmic" },
  { value: "sqrt", label: "Square root" },
  { value: "pow", label: "Power" },
]

// Outlier-filter fence width. Values are the k in Tukey's k×IQR fences.
const SENSITIVITY_OPTIONS: SelectDropdownOption[] = [
  {
    value: String(DEFAULT_OUTLIER_IQR_MULTIPLIER),
    label: "Standard",
    description: "1.5× IQR — mild + extreme",
  },
  {
    value: String(CONSERVATIVE_OUTLIER_IQR_MULTIPLIER),
    label: "Conservative",
    description: "3× IQR — extremes only",
  },
]

function metricOption(record: MetricRecord): SelectDropdownOption {
  return {
    value: record.id,
    label: record.metric_display_name || record.metric_name,
  }
}

function byLabel(a: SelectDropdownOption, b: SelectDropdownOption): number {
  return a.label.localeCompare(b.label)
}

export function ScatterConfigControls({
  config,
  metricRecordsById,
  compareBy,
  onChange,
}: PlotConfigControlsProps) {
  const records = useMemo(
    () => (metricRecordsById ? Object.values(metricRecordsById) : []),
    [metricRecordsById],
  )
  const options = useMemo<SelectDropdownOption[]>(
    () =>
      records
        .filter(
          (record): record is MetricRecord =>
            record?.output_type === "float" || record?.output_type === "string",
        )
        .map((record) => ({
          ...metricOption(record),
          description:
            record.output_type === "string" ? "Categorical" : "Numeric",
        }))
        .sort(byLabel),
    [records],
  )
  // Bubble size needs a numeric value, so exclude categorical metrics here.
  const numericOptions = useMemo<SelectDropdownOption[]>(
    () =>
      records
        .filter(
          (record): record is MetricRecord => record?.output_type === "float",
        )
        .map(metricOption)
        .sort(byLabel),
    [records],
  )

  return (
    <div className="flex flex-col gap-3">
      <AxisPicker
        label="X axis"
        value={config.xAxisMetricId}
        options={options}
        onSelect={(value) => onChange({ ...config, xAxisMetricId: value })}
      />
      {config.xAxisMetricId && (
        <ScaleControl
          label="X scale"
          scale={config.xAxisScale}
          exponent={config.xAxisScaleExponent}
          onScaleChange={(scale) => onChange({ ...config, xAxisScale: scale })}
          onExponentChange={(exponent) =>
            onChange({ ...config, xAxisScaleExponent: exponent })
          }
        />
      )}
      <AxisPicker
        label="Y axis"
        value={config.yAxisMetricId}
        options={options}
        onSelect={(value) => onChange({ ...config, yAxisMetricId: value })}
      />
      {config.yAxisMetricId && (
        <ScaleControl
          label="Y scale"
          scale={config.yAxisScale}
          exponent={config.yAxisScaleExponent}
          onScaleChange={(scale) => onChange({ ...config, yAxisScale: scale })}
          onExponentChange={(exponent) =>
            onChange({ ...config, yAxisScaleExponent: exponent })
          }
        />
      )}
      <AxisPicker
        label="Size (optional)"
        value={config.zAxisMetricId ?? ""}
        options={[{ value: "", label: "None" }, ...numericOptions]}
        onSelect={(value) =>
          onChange({ ...config, zAxisMetricId: value || undefined })
        }
      />
      {config.zAxisMetricId && (
        <ScaleControl
          label="Size scale"
          scale={config.zAxisScale}
          exponent={config.zAxisScaleExponent}
          onScaleChange={(scale) => onChange({ ...config, zAxisScale: scale })}
          onExponentChange={(exponent) =>
            onChange({ ...config, zAxisScaleExponent: exponent })
          }
        />
      )}
      <PlotToggle
        label="Trend line"
        checked={config.showTrendLine ?? false}
        onCheckedChange={(checked) =>
          onChange({ ...config, showTrendLine: checked })
        }
      />
      <PlotToggle
        label="Exclude outliers"
        checked={config.excludeOutliers ?? false}
        onCheckedChange={(checked) =>
          onChange({ ...config, excludeOutliers: checked })
        }
      />
      {config.excludeOutliers && (
        <SensitivityControl
          multiplier={config.outlierIqrMultiplier}
          onChange={(multiplier) =>
            onChange({ ...config, outlierIqrMultiplier: multiplier })
          }
        />
      )}
      {/* A legend labels Compare By groups, so it only applies while one is active. */}
      {compareBy && (
        <>
          <PlotToggle
            label="Legend"
            checked={config.showLegend ?? false}
            onCheckedChange={(checked) =>
              onChange({ ...config, showLegend: checked })
            }
          />
          {config.showLegend &&
            Object.keys(compareBy.colors).length >
              LEGEND_GROUP_WARN_THRESHOLD && (
              <p className="text-xs text-coval-warning-text">
                This Compare By has {Object.keys(compareBy.colors).length}{" "}
                groups — the legend may overflow its two lines and scroll.
              </p>
            )}
        </>
      )}
    </div>
  )
}

function PlotToggle({
  label,
  checked,
  onCheckedChange,
}: {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-xs font-medium text-coval-text-muted">{label}</span>
      <Switch
        aria-label={label}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
    </div>
  )
}

function AxisPicker({
  label,
  value,
  options,
  onSelect,
}: {
  label: string
  value: string | null | undefined
  options: SelectDropdownOption[]
  onSelect: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-coval-text-muted">{label}</span>
      <SelectDropdown
        aria-label={`${label} metric`}
        options={options}
        value={value ?? undefined}
        onValueChange={onSelect}
        placeholder="Select a metric"
        searchable
      />
    </div>
  )
}

// Preset dropdown for the outlier fence width; only shown while the filter is on.
// A non-preset persisted multiplier falls back to the Standard display.
function SensitivityControl({
  multiplier,
  onChange,
}: {
  multiplier: number | undefined
  onChange: (multiplier: number) => void
}) {
  const value =
    multiplier === CONSERVATIVE_OUTLIER_IQR_MULTIPLIER
      ? String(CONSERVATIVE_OUTLIER_IQR_MULTIPLIER)
      : String(DEFAULT_OUTLIER_IQR_MULTIPLIER)
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-coval-text-muted">
        Outlier sensitivity
      </span>
      <SelectDropdown
        aria-label="Outlier sensitivity"
        options={SENSITIVITY_OPTIONS}
        value={value}
        onValueChange={(next) => onChange(Number(next))}
      />
    </div>
  )
}

function ScaleControl({
  label,
  scale,
  exponent,
  onScaleChange,
  onExponentChange,
}: {
  label: string
  scale: ComparisonPlotScale | undefined
  exponent: number | undefined
  onScaleChange: (scale: ComparisonPlotScale) => void
  onExponentChange: (exponent: number) => void
}) {
  const committed = String(exponent ?? DEFAULT_POW_EXPONENT)
  // Local draft so the field accepts intermediate text (empty, "1.") without the
  // controlled value snapping back; only positive numbers commit to the config.
  const [draft, setDraft] = useState(committed)
  useEffect(() => {
    setDraft(committed)
  }, [committed])

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-coval-text-muted">{label}</span>
      <div className="flex items-center gap-2">
        <SelectDropdown
          aria-label={label}
          options={SCALE_OPTIONS}
          value={scale ?? "linear"}
          onValueChange={(value) => onScaleChange(value as ComparisonPlotScale)}
        />
        {scale === "pow" && (
          <Input
            type="number"
            aria-label={`${label} exponent`}
            className="w-20"
            step={1}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
              const next = Number(event.target.value)
              if (Number.isFinite(next) && next > 0) {
                onExponentChange(next)
              }
            }}
            onBlur={() => setDraft(committed)}
          />
        )}
      </div>
    </div>
  )
}
